"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Button } from "@/components/ui";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { formatCurrency, formatDate } from "@/lib/format-data";
import { type PurchaseRow, flattenPurchases, groupPurchasesByDate, purchaseItemName } from "@/lib/patient-purchases";
import { cx } from "@/lib/tailwind-utils";
import type { PatientDetail } from "@/types/patient";
import { chargeQueryOptions } from "@/api/provider/billing/charge-query";
const GROUPS_PER_PAGE = 5;
interface PurchasesSectionProps {
  patient: PatientDetail;
}
export default function PurchasesSection(t0) {
  const $ = _c(10);
  const {
    patient
  } = t0;
  let t1;
  if ($[0] !== patient.id) {
    t1 = chargeQueryOptions.table(patient.id);
    $[0] = patient.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2
  } = useQuery(t1);
  const charges = t2 === undefined ? [] : t2;
  const [page, setPage] = useState(0);
  const rows = flattenPurchases(charges);
  const clock = useClinicClock();
  const groups = groupPurchasesByDate(rows, formatDate, clock);
  const pageCount = Math.max(1, Math.ceil(groups.length / GROUPS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * GROUPS_PER_PAGE;
  const visibleGroups = groups.slice(pageStart, pageStart + GROUPS_PER_PAGE);
  let t3;
  let t4;
  if ($[2] !== page || $[3] !== pageCount) {
    t3 = () => {
      if (page > pageCount - 1) {
        setPage(0);
      }
    };
    t4 = [page, pageCount];
    $[2] = page;
    $[3] = pageCount;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const T0 = CollapsibleSection;
  const t5 = "Purchases";
  const t6 = "purchases";
  const t7 = rows.length > 0 && <span className="text-caption text-muted-foreground">{rows.length} <span>{rows.length === 1 ? "item" : "items"}</span></span>;
  const t8 = rows.length === 0 ? <div className="py-6 text-center"><p className="text-body text-muted-foreground">No purchases yet</p></div> : <><div className="space-y-5">{visibleGroups.map(_temp3)}</div>{pageCount > 1 && <div className="mt-4 flex items-center justify-between border-t pt-3"><p className="text-caption text-muted-foreground">Showing {pageStart + 1}–{pageStart + visibleGroups.length} of{" "}{groups.length}{" "}<span>{groups.length === 1 ? "day" : "days"}</span></p><div className="flex items-center gap-2"><Button variant="secondary" size="sm" icon={FiChevronLeft} onClick={() => setPage(_temp4)} disabled={safePage === 0}>Previous</Button><span className="text-caption tabular-nums text-muted-foreground">Page {safePage + 1} of {pageCount}</span><Button variant="secondary" size="sm" onClick={() => setPage(p_0 => Math.min(pageCount - 1, p_0 + 1))} disabled={safePage >= pageCount - 1}>Next<FiChevronRight className="ml-1.5 shrink-0" /></Button></div></div>}</>;
  let t9;
  if ($[6] !== T0 || $[7] !== t7 || $[8] !== t8) {
    t9 = <T0 title={t5} persistKey={t6} headerControls={t7}>{t8}</T0>;
    $[6] = T0;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function _temp4(p) {
  return Math.max(0, p - 1);
}
function _temp3(group) {
  const groupTotal = group.rows.reduce(_temp, 0);
  return <div key={group.dateKey || "undated"}><div className="mb-1 flex items-baseline justify-between"><p className="text-caption-strong uppercase tracking-wide text-muted-foreground">{group.label}</p><p className="text-caption tabular-nums text-muted-foreground">{formatCurrency(groupTotal)}</p></div><ul className="divide-y divide-border">{group.rows.map(_temp2)}</ul></div>;
}
function _temp2(row) {
  return <PurchaseRowItem key={row.key} row={row} />;
}
function _temp(sum, r) {
  return r.isReversed ? sum : sum + r.finalCents;
}
function PurchaseRowItem(t0) {
  const $ = _c(43);
  const {
    row
  } = t0;
  const {
    item,
    locationName,
    isReversed,
    quantity,
    unitPrice,
    originalCents,
    discountCents,
    taxCents,
    finalCents
  } = row;
  let meta;
  if ($[0] !== locationName || $[1] !== quantity || $[2] !== unitPrice) {
    meta = [];
    if (quantity !== 1) {
      let t1;
      if ($[4] !== unitPrice) {
        t1 = formatCurrency(unitPrice);
        $[4] = unitPrice;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      meta.push(`${quantity} × ${t1}`);
    }
    if (locationName) {
      meta.push(locationName);
    }
    $[0] = locationName;
    $[1] = quantity;
    $[2] = unitPrice;
    $[3] = meta;
  } else {
    meta = $[3];
  }
  const hasDiscount = discountCents > 0;
  const t1 = isReversed ? "text-muted-foreground line-through" : "text-foreground";
  let t2;
  if ($[6] !== t1) {
    t2 = cx("text-body", t1);
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== item) {
    t3 = purchaseItemName(item);
    $[8] = item;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <p className={t2}>{t3}</p>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== meta) {
    t5 = meta.length > 0 && <p className="mt-0.5 text-caption text-muted-foreground">{meta.join(" \xB7 ")}</p>;
    $[13] = meta;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== item.description) {
    t6 = item.description && <p className="mt-0.5 text-caption text-muted-foreground">{item.description}</p>;
    $[15] = item.description;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = <div className="min-w-0 flex-1">{t4}{t5}{t6}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = isReversed ? "text-muted-foreground line-through" : "text-foreground";
  let t9;
  if ($[21] !== t8) {
    t9 = cx("text-body tabular-nums", t8);
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== finalCents) {
    t10 = formatCurrency(finalCents);
    $[23] = finalCents;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <p className={t9}>{t10}</p>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== discountCents || $[29] !== hasDiscount || $[30] !== isReversed || $[31] !== originalCents) {
    t12 = hasDiscount && !isReversed && <p className="text-caption tabular-nums text-muted-foreground"><span className="line-through">{formatCurrency(originalCents)}</span><span className="ml-1.5 text-status-success-text">−{formatCurrency(discountCents)}</span></p>;
    $[28] = discountCents;
    $[29] = hasDiscount;
    $[30] = isReversed;
    $[31] = originalCents;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== isReversed || $[34] !== taxCents) {
    t13 = taxCents > 0 && !isReversed && <p className="text-caption tabular-nums text-muted-foreground">incl. {formatCurrency(taxCents)} tax</p>;
    $[33] = isReversed;
    $[34] = taxCents;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t13) {
    t14 = <div className="flex shrink-0 flex-col items-end">{t11}{t12}{t13}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t14 || $[41] !== t7) {
    t15 = <li className="flex items-start justify-between gap-4 py-2">{t7}{t14}</li>;
    $[40] = t14;
    $[41] = t7;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
}